/**
 * GlobalTopBar — thin breadcrumb path bar that scrolls with the page.
 * Slash-separated, no leading slash. Segments with `copyable: true` reveal a
 * copy icon on hover and copy their label to the clipboard on click.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Copy, Check } from 'lucide-react';
import { useBreadcrumbSegments, useTopBarHidden, useBreadcrumbActionsNode } from 'shared/context/BreadcrumbContext';

function CopyableSegment({ label }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    try {
      navigator.clipboard?.writeText(String(label));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* ignore */
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title="Copy"
      className="group inline-flex min-w-0 items-center gap-1.5 text-[13px] font-medium text-[color:var(--text-primary)]"
    >
      <span className="truncate">{label}</span>
      {copied ? (
        <Check size={12} className="shrink-0 text-[color:var(--feedback-success)]" />
      ) : (
        <Copy
          size={12}
          className="shrink-0 text-[color:var(--text-muted)] opacity-0 transition-opacity group-hover:opacity-100"
        />
      )}
    </button>
  );
}

export default function GlobalTopBar({ onMobileMenuToggle, isMobileMenuOpen }) {
  const segments = useBreadcrumbSegments();
  const hidden = useTopBarHidden();
  const actions = useBreadcrumbActionsNode();
  if (hidden) return null;

  return (
    <header
      className="flex h-8 shrink-0 items-center bg-surface px-4 pt-1"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      {/* Mobile hamburger */}
      {onMobileMenuToggle && (
        <button
          type="button"
          onClick={onMobileMenuToggle}
          className="mr-3 flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] transition-colors md:hidden"
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <Menu size={16} />
        </button>
      )}

      {/* Breadcrumb: slash separated between segments, no leading slash. */}
      <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1.5">
        {segments.map((seg, i) => {
          const isLast = i === segments.length - 1;
          return (
            <React.Fragment key={i}>
              {i > 0 && (
                <span className="shrink-0 text-[13px] text-[color:var(--text-muted)]" aria-hidden>/</span>
              )}
              {isLast ? (
                seg.copyable ? (
                  <CopyableSegment label={seg.label} />
                ) : (
                  <span className="truncate text-[13px] font-medium text-[color:var(--text-primary)]">
                    {seg.label}
                  </span>
                )
              ) : (
                <Link
                  to={seg.to || '#'}
                  className="shrink-0 text-[13px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
                >
                  {seg.label}
                </Link>
              )}
            </React.Fragment>
          );
        })}
      </nav>

      {/* Right-side actions injected by the current page via useBreadcrumbActions().
          `min-w-0` and shrinkable, not `shrink-0`: pages like Capabilities inject
          a whole toolbar here (~660px of count chip + view toggle + search +
          agent filter), and a non-shrinking slot pushed it straight off the
          right edge on a narrow viewport instead of letting it compress. */}
      {actions && (
        <div className="ml-3 flex min-w-0 items-center justify-end">
          {actions}
        </div>
      )}
    </header>
  );
}
